import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Search, AlarmClock, ChevronLeft, Layers, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { STATUS, PRIO, fmt, errMsg } from "./legalUtils";
import { useBulkSelect, RowCheck, BulkBar } from "../common/BulkSelect";
import { LegalCountdown } from "./LegalCountdown";

export const CaseRow = ({ c, onOpen, onDelete, check }) => (
  <div role="button" tabIndex={0} onClick={() => onOpen(c.id)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(c.id); }} data-testid={`legal-case-${c.id}`}
    className={`group flex w-full cursor-pointer items-center gap-3 rounded-2xl border bg-white p-3 text-right transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md ${c.overdue ? "border-rose-300" : "border-slate-200"}`}>
    {check}
    <div className="w-28 shrink-0">
      <p className="font-mono text-xs font-bold text-[#0f1f3d]" dir="ltr">{c.number}</p>
      <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS[c.status]?.[1]}`}>{STATUS[c.status]?.[0]}</span>
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate font-heading font-bold text-slate-900">{c.title}</p>
      <p className="truncate text-[11px] text-slate-500">{c.category}{c.employee_name ? ` · ${c.employee_name}` : ""}{c.result ? ` · النتيجة: ${c.result}` : ""}</p>
    </div>
    <div className="hidden text-left text-[11px] text-slate-500 sm:block">
      <p>لدى: <b className="text-slate-700">{c.holder_name}</b></p>
      <p className="flex items-center justify-end gap-1"><Layers className="h-3 w-3" /> {c.levels} تصعيد · <span className={PRIO[c.priority]?.[1]}>{PRIO[c.priority]?.[0]}</span></p>
    </div>
    <div className="w-24 text-left text-[11px]">
      {c.status !== "closed" ? <p className={`flex items-center justify-end gap-1 font-semibold ${c.overdue ? "text-rose-600" : "text-slate-500"}`}><AlarmClock className="h-3.5 w-3.5" />{c.timer?.deadline ? c.timer.deadline.slice(0, 10) : c.due_date}</p>
        : <p className="text-slate-400">{fmt(c.updated_at)}</p>}
      {c.status !== "closed" && c.timer && <div className="mt-1 flex justify-end"><LegalCountdown timer={c.timer} compact testId={`legal-row-countdown-${c.id}`} /></div>}
    </div>
    {onDelete && (
      <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(c); }} title="حذف الحالة" data-testid={`legal-case-delete-${c.id}`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
    )}
    <ChevronLeft className="h-4 w-4 text-slate-300 group-hover:text-slate-600" />
  </div>
);

export const LegalCasesList = ({ tick, onOpen, onNew, scope, setScope, onChanged }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const { user } = useAuth();
  const bulk = useBulkSelect();
  const deletable = (rows || []).filter((c) => user?.role === "manager" || c.created_by_id === user?.user_id).map((c) => c.id);
  const del = async (c) => {
    if (!window.confirm(`حذف الحالة ${c.number} «${c.title}» نهائياً؟ لا يمكن التراجع.`)) return;
    try { await api.legalDelete(c.id); setRows((r) => r.filter((x) => x.id !== c.id)); toast.success("تم حذف الحالة"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    const byTimer = (items) => [...(items || [])].sort((a, b) => {
      const ta = a.status !== "closed" && a.timer?.deadline ? a.timer.deadline : "~", tb = b.status !== "closed" && b.timer?.deadline ? b.timer.deadline : "~";
      return ta === tb ? 0 : ta < tb ? -1 : 1;
    });
    const t = setTimeout(() => api.legalCases(scope === "overdue" ? "active" : scope, q).then((r) => setRows(byTimer(scope === "overdue" ? (r.items || []).filter((c) => c.overdue) : r.items))).catch(() => setRows([])), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [scope, q, tick]);
  const tab = (k, l) => <button onClick={() => setScope(k)} data-testid={`legal-scope-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${scope === k ? "bg-[#0f1f3d] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {tab("active", "كل الجارية")}{tab("mine", "لديّ الآن")}{tab("overdue", "المتأخرة")}
        <div className="relative ms-auto"><Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو الإقامة أو رقم الحالة أو المعرّف" className="h-9 w-72 pr-8" data-testid="legal-search" /></div>
        <Button onClick={onNew} className="gap-1.5 bg-amber-500 text-[#0f1f3d] hover:bg-amber-400" data-testid="legal-new-btn"><Plus className="h-4 w-4" /> فتح حالة</Button>
      </div>
      {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : rows.length === 0 ? <p className="rounded-2xl border border-dashed py-14 text-center text-sm text-slate-400" data-testid="legal-empty">لا توجد حالات</p>
          : <div className="space-y-2">
            {deletable.length > 0 && <BulkBar bulk={bulk} allIds={deletable} noun="حالة" prefix="legal-bulk" note={user?.role === "manager" ? "" : "تحذف الحالات التي فتحتها فقط"}
              onDelete={async (ids) => { const r = await api.legalBulkDelete(ids); onChanged?.(); return r; }} />}
            {rows.map((c) => { const can = deletable.includes(c.id); return <CaseRow key={c.id} c={c} onOpen={onOpen} onDelete={can ? del : undefined}
              check={can ? <RowCheck checked={bulk.sel.has(c.id)} onChange={() => bulk.toggle(c.id)} testid={`legal-check-${c.id}`} /> : null} />; })}</div>}
    </div>
  );
};
